// src/components/GlobalCORSModal.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  LinearProgress
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  addApiStatusListener,
  isApiCurrentlyDown,
  forceHealthCheck
} from '../../apis/Agency';

const GlobalCORSModal = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [checkAttempt, setCheckAttempt] = useState(0);

  // Define public routes that should NOT show the modal
  const publicRoutes = ['/', '/agency-signin', '/agency-resetpassword'];

  // Check if current route is public
  const isPublicRoute = () => {
    return publicRoutes.some(
      route =>
        location.pathname === route ||
        location.pathname.startsWith('/reset-password/') ||
        location.pathname.startsWith('/reset/')
    );
  };

  useEffect(() => {
    let countdownInterval = null;
    let healthCheckInterval = null;

    // Don't show modal on public routes
    if (isPublicRoute()) {
      return;
    }

    const unsubscribe = addApiStatusListener(error => {
      // Don't show modal on public routes
      if (isPublicRoute()) return;

      if (error) {
        setOpen(true);
        setIsRecovering(false);
        setCountdown(30);
        startCountdown();
      } else {
        setIsRecovering(true);
        setTimeout(() => {
          setOpen(false);
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }, 2000);
      }
    });

    const startCountdown = () => {
      if (countdownInterval) clearInterval(countdownInterval);

      countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            handleRefresh();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const handleRefresh = () => {
      window.location.reload();
    };

    // Only show modal on non-public routes when API is down
    if (!isPublicRoute() && isApiCurrentlyDown()) {
      setOpen(true);
      startCountdown();
    }

    if (open && !isRecovering && !isPublicRoute()) {
      healthCheckInterval = setInterval(async () => {
        setCheckAttempt(prev => prev + 1);
        const isHealthy = await forceHealthCheck();
        if (isHealthy) {
          clearInterval(healthCheckInterval);
        }
      }, 5000);
    }

    return () => {
      unsubscribe();
      if (countdownInterval) clearInterval(countdownInterval);
      if (healthCheckInterval) clearInterval(healthCheckInterval);
    };
  }, [open, isRecovering, location.pathname]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleForceCheck = async () => {
    const isHealthy = await forceHealthCheck();
    if (isHealthy) {
      window.location.reload();
    }
  };

  // Don't render anything on public routes
  if (isPublicRoute()) {
    return null;
  }

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth='sm'
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        {isRecovering ? (
          <CheckCircleIcon sx={{ fontSize: 70, color: '#4caf50', mb: 1 }} />
        ) : (
          <CloudOffIcon sx={{ fontSize: 70, color: '#f44336', mb: 1 }} />
        )}
        <Typography variant='h5' fontWeight='bold'>
          {isRecovering ? 'System Recovery' : 'System Under Maintenance'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {isRecovering ? (
            <>
              <CircularProgress size={50} sx={{ mb: 2, color: '#4caf50' }} />
              <Typography variant='body1' gutterBottom color='success.main'>
                API services are back online!
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Refreshing the page...
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress size={50} sx={{ mb: 2, color: '#ff9800' }} />
              <Typography variant='body1' gutterBottom>
                System is currently being updated.
              </Typography>
              <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                Please wait while we restore services...
              </Typography>

              <Typography
                variant='caption'
                color='textSecondary'
                sx={{ display: 'block', mt: 1 }}
              >
                Auto-checking for recovery
              </Typography>
              <Box sx={{ mt: 3, width: '100%' }}>
                <LinearProgress
                  variant='determinate'
                  value={((30 - countdown) / 30) * 100}
                />
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  Auto-refreshing in {countdown} seconds
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
        {!isRecovering && (
          <>
            <Button
              variant='outlined'
              onClick={handleForceCheck}
              startIcon={<RefreshIcon />}
            >
              Check Now
            </Button>
            <Button variant='contained' onClick={handleRefresh} sx={{ px: 4 }}>
              Refresh Now
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GlobalCORSModal;
